import React, { useMemo, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { IconButton, Stack, Tabs, Tooltip } from "@mui/material";
import { DraggableTabItem } from "./DraggableTabItem";
import { DragDropTabsProps, Tab } from "./types";
import { Cancel, Save } from "@mui/icons-material";
import { useTranslation } from "../../../../hooks/useTranslation";

const DragDropTabs: React.FC<DragDropTabsProps> = ({
  tabs,
  translate,
  activeTabPath,
  onSaveTabs,
  onClose,
}) => {
  const { translateWithoutPrefix } = useTranslation();
  const [editedTabs, setEditedTabs] = useState<Tab[]>(tabs);
  const activeIndex = editedTabs.findIndex((tab) => tab.path === activeTabPath);
  const allTabsHidden = useMemo(
    () => editedTabs.every((tab) => tab.hidden),
    [editedTabs],
  );

  const toggleVisibility = (path: string) => {
    setEditedTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.path === path ? { ...tab, hidden: !tab.hidden } : tab,
      ),
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    const reorderedTabs = reorderTabs(
      source.index,
      destination.index,
      editedTabs,
    );

    setEditedTabs(reorderedTabs);
  };

  const reorderTabs = (
    sourceIndex: number,
    destinationIndex: number,
    tabs: Tab[],
  ): Tab[] => {
    const reorderedTabs = Array.from(tabs);
    const [removed] = reorderedTabs.splice(sourceIndex, 1);
    reorderedTabs.splice(destinationIndex, 0, removed);
    return reorderedTabs;
  };

  const handleCancel = () => {
    setEditedTabs(tabs);
    onClose();
  };

  const handleSave = () => {
    onSaveTabs(editedTabs);
    handleCancel();
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="tabsId" type="group" direction="horizontal">
        {(provided) => (
          <Tabs
            {...provided.droppableProps}
            ref={provided.innerRef}
            variant="scrollable"
            value={activeIndex}
            sx={{ flexGrow: 1 }}
          >
            {editedTabs.map((tab, index) => (
              <DraggableTabItem
                key={tab.path}
                tab={tab}
                index={index}
                translate={translate}
                toggleVisibility={toggleVisibility}
              />
            ))}
            {provided.placeholder}
          </Tabs>
        )}
      </Droppable>

      <Stack direction={"row"} alignItems={"center"}>
        <IconButton color="error" onClick={handleCancel}>
          <Cancel fontSize="small" />
        </IconButton>
        <Tooltip
          title={
            allTabsHidden && translateWithoutPrefix("pages.error.allHidden")
          }
          arrow
        >
          <span>
            <IconButton
              color="primary"
              onClick={handleSave}
              disabled={allTabsHidden}
            >
              <Save fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </DragDropContext>
  );
};

export default DragDropTabs;
